/**
 * CommitmentsZurGenehmigung — Genehmiger-Dashboard für ausstehende Vereinbarungen.
 *
 * @page /commitments/zur-genehmigung
 * @api /api/commitments/zur-genehmigung
 * @i18n commitment.*
 * @since v2.408.0 / #930
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Loader2, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fmtDate } from './Commitments';

function api(path) {
  return fetch(path, { credentials: 'include' }).then(async (r) => {
    let d; try { d = await r.json(); } catch { throw new Error(`HTTP ${r.status}`); }
    if (!r.ok) throw new Error(d.message || d.error || `HTTP ${r.status}`);
    return d;
  });
}

export default function CommitmentsZurGenehmigung() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('/api/commitments/zur-genehmigung');
      setItems(d.commitments || d.items || d || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="px-6 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/commitments" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft className="w-4 h-4" /> {t('commitment.back')}
        </Link>
      </div>
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-[#338edc]" />
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{t('commitment.approver_title')}</h1>
      </div>

      {err && <div className="px-3 py-2 rounded-lg bg-rose-50 text-rose-700 text-sm">{err}</div>}

      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-tertiary)]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('commitment.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-tertiary)]">{t('commitment.approver_empty')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-line text-[var(--text-secondary)] text-xs uppercase tracking-wide">
                <th className="px-4 py-2">{t('commitment.col.nr')}</th>
                <th className="px-4 py-2">{t('commitment.col.titel')}</th>
                <th className="px-4 py-2">{t('commitment.col.kunde')}</th>
                <th className="px-4 py-2">{t('commitment.col.eingereicht_von')}</th>
                <th className="px-4 py-2">{t('commitment.col.eingereicht_am')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-line odd:bg-[var(--bg-inset)]/30 hover:bg-[var(--bg-inset)]">
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link to={`/commitments/${c.id}`} className="text-[#338edc] hover:underline">{c.commitment_nr}</Link>
                  </td>
                  <td className="px-4 py-2">{c.titel}</td>
                  <td className="px-4 py-2 text-[var(--text-secondary)]">{c.kunde_name}</td>
                  <td className="px-4 py-2 text-[var(--text-secondary)]">{c.erstellt_von_name || '—'}</td>
                  <td className="px-4 py-2 text-[var(--text-secondary)]">{fmtDate(c.eingereicht_am || c.aktualisiert_am)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
